import { addDoc, collection, deleteDoc, doc, getDocs, query, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import { parseContents } from '../utils';
import { ProjectErrorType } from '../../utils/errors/types';
import { getProjectErrorFromRawError } from '../../utils/errors';
import { MentorBoard } from '../types/mentorBoard';
import { parseMentorBoards } from './parser';
import { CreateMentorBoardDto, UpdateMentorBoardDto } from '../typeDtos/mentorBoard';

export const getAllMentorBoards = async (): Promise<MentorBoard[]> => {
  try {
    const _query = query(collection(db, 'mentorBoards'));
    const rawResult = await getDocs(_query);
    const boards = await parseMentorBoards(parseContents(rawResult));
    // Boards without an order go last.
    return boards.sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER));
  } catch (error) {
    console.log('[MENTOR BOARDS] Error at getAllMentorBoards:', error);
    return [];
  }
};

export const createMentorBoard = async (
  data: CreateMentorBoardDto,
): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    // New boards are appended after the current last one.
    const boards = await getAllMentorBoards();
    const order = boards.reduce((max, board) => Math.max(max, board.order ?? -1), -1) + 1;
    await addDoc(collection(db, 'mentorBoards'), { ...data, order });
    return { success: true };
  } catch (e) {
    console.log('[MENTOR BOARDS] Error at createMentorBoard:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const updateMentorBoard = async (
  data: UpdateMentorBoardDto,
): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const docRef = doc(db, 'mentorBoards', data.id);
    await updateDoc(docRef, { ...data });
    return { success: true };
  } catch (e) {
    console.log('[MENTOR BOARDS] Error at updateMentorBoard:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

// Persists the given id sequence as the display order (0, 1, 2, ...).
export const reorderMentorBoards = async (orderedIds: string[]): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const batch = writeBatch(db);
    orderedIds.forEach((id, index) => batch.update(doc(db, 'mentorBoards', id), { order: index }));
    await batch.commit();
    return { success: true };
  } catch (e) {
    console.log('[MENTOR BOARDS] Error at reorderMentorBoards:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const deleteMentorBoard = async (id: string): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    await deleteDoc(doc(db, 'mentorBoards', id));
    return { success: true };
  } catch (e) {
    console.log('[MENTOR BOARDS] Error at deleteMentorBoard:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};
